import React from 'react';
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  Stack,
  Button,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack as BackIcon,
  Chat as ChatIcon,
  MenuBook as BrowseIcon,
} from '@mui/icons-material';
import { useHelpContext } from '../../contexts/HelpWidgetContext';
import { useContextualArticles, usePopularArticles } from '../../hooks/useHelp';
import { useAuth } from '../../contexts/AuthContext';
import { SearchInput } from './SearchInput';
import { SearchResults } from './SearchResults';
import { ArticleViewer } from './ArticleViewer';
import { ChatContainer } from './ChatContainer';

export const HelpWidget: React.FC = () => {
  const {
    isOpen,
    view,
    searchQuery,
    selectedArticle,
    closeHelp,
    setView,
    setSearchQuery,
    selectArticle,
    goBack,
    currentPage,
  } = useHelpContext();

  const { organization } = useAuth();
  const userTier = organization?.subscription?.tier || 'free';

  // Fetch contextual and popular articles for home view
  const { data: contextualData } = useContextualArticles(currentPage, userTier, {
    enabled: isOpen && view === 'home',
  });
  const { data: popularData } = usePopularArticles(5);

  const contextualArticles = contextualData || [];
  const popularArticles = popularData || [];

  const renderContent = () => {
    switch (view) {
      case 'search':
        return (
          <SearchResults
            query={searchQuery}
            onSelectArticle={selectArticle}
          />
        );

      case 'article':
        return selectedArticle ? (
          <ArticleViewer
            slug={selectedArticle}
            onSelectRelated={selectArticle}
          />
        ) : null;

      case 'chat':
        return <ChatContainer />;

      case 'home':
      default:
        return (
          <Box>
            {/* Quick Actions */}
            <Stack direction="row" spacing={1} mb={3}>
              <Button
                variant="outlined"
                startIcon={<ChatIcon />}
                onClick={() => setView('chat')}
                fullWidth
              >
                Chat with AI
              </Button>
              <Button
                variant="outlined"
                startIcon={<BrowseIcon />}
                onClick={() => setView('search')}
                fullWidth
              >
                Browse Help
              </Button>
            </Stack>

            {/* Contextual Articles */}
            {contextualArticles.length > 0 && (
              <Box mb={3}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  📍 FOR THIS PAGE
                </Typography>
                <Stack spacing={1}>
                  {contextualArticles.map((article: any) => (
                    <Box
                      key={article.slug}
                      onClick={() => selectArticle(article.slug)}
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Typography variant="body2" fontWeight={500}>
                        {article.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {article.summary.substring(0, 80)}...
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Popular Articles */}
            {popularArticles.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  🔥 POPULAR ARTICLES
                </Typography>
                <Stack spacing={1}>
                  {popularArticles.map((article: any) => (
                    <Box
                      key={article.slug}
                      onClick={() => selectArticle(article.slug)}
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Typography variant="body2" fontWeight={500}>
                        {article.title}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        );
    }
  };

  const getTitle = () => {
    switch (view) {
      case 'search':
        return 'Search Results';
      case 'article':
        return 'Help Article';
      case 'chat':
        return 'Chat with AI';
      default:
        return 'Help';
    }
  };

  const showBackButton = view !== 'home';

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={closeHelp}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 420 },
          maxWidth: '100%',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          bgcolor: 'background.paper',
          zIndex: 1,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            {showBackButton && (
              <IconButton size="small" onClick={goBack}>
                <BackIcon />
              </IconButton>
            )}
            <Typography variant="h6" fontWeight={600}>
              {getTitle()}
            </Typography>
          </Stack>
          <IconButton size="small" onClick={closeHelp}>
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* Search Input (always visible except in chat/article) */}
        {view !== 'chat' && view !== 'article' && (
          <Box mt={2}>
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              onClear={() => setSearchQuery('')}
            />
          </Box>
        )}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {renderContent()}
      </Box>
    </Drawer>
  );
};

export default HelpWidget;
